import { QuizQuestionInput } from "@verity/api-client/types";
import apiClient from "../../../../../helpers/api-client";
import i18n from "../../../../../i18n";

type QuizSessionData = Awaited<ReturnType<typeof apiClient.proctoringSession.getInstructorQuizSessionData>>;

// ── Quiz session data types ───────────────────────────────────────────────────

export type QuizQuestion = QuizQuestionInput;

type QuizResponse = QuizSessionData["responses"][number];

export type QuizResponseResult = Pick<QuizResponse, "id" | "questionNo" | "isAnswered" | "answer"> & {
	score: number;
	notes: string;
	publishedScore: number | null;
	publishedNotes: string | null;
	hasUnpublishedChanges: boolean;
};

export type DraftGradeError = "invalidScore" | "scoreExceedsMax" | "scoreNegative" | "scoreRequired";

export type DraftGrade = {
	score: string;
	notes: string;
	error?: DraftGradeError;
};

export function getDraftGradeErrorMessage(error: DraftGradeError | undefined) {
	switch (error) {
		case "invalidScore":
			return i18n.t("result.components.resultsPage.proctoringResultsViewTSX.invalidScore") as string;
		case "scoreExceedsMax":
			return i18n.t("result.components.resultsPage.proctoringResultsViewTSX.scoreExceedsMax") as string;
		case "scoreNegative":
			return i18n.t("result.components.resultsPage.proctoringResultsViewTSX.scoreNegative") as string;
		case "scoreRequired":
			return i18n.t("result.components.resultsPage.proctoringResultsViewTSX.scoreRequired") as string;
		default:
			return undefined;
	}
}

export function mapQuizDataToResults(data: QuizSessionData) {
	const sortedQuestions = [...data.questions].sort((a, b) => a.questionOrder - b.questionOrder) as QuizQuestion[];
	const mappedResponses: QuizResponseResult[] = data.responses.map((r) => ({
		id: r.id,
		questionNo: r.questionNo,
		isAnswered: r.isAnswered,
		answer: r.answer,
		score: r.grading.score,
		notes: r.grading.notes,
		publishedScore: r.grading.publishedScore ?? null,
		publishedNotes: r.grading.publishedNotes ?? null,
		hasUnpublishedChanges: r.grading.hasUnpublishedChanges,
	}));
	return { sortedQuestions, mappedResponses };
}

/**Assuming all validated */
export function prepareResponsesForSave(draftGrades: Record<number, DraftGrade>, responses: QuizResponseResult[]) {
	return responses.map((r) => {
		const draft = getGradeDraftFromResponse(draftGrades, r);

		return {
			id: r.id,
			score: typeof draft?.score === "string" ? Number(draft.score) : r.score,
			notes: typeof draft?.notes === "string" ? draft.notes : r.notes,
		};
	});
}

export function validateDraftGrade(score: string, maxGrade: number): DraftGradeError | undefined {
	if (maxGrade === 0) {
		// If max points is 0, we can skip all validation
		return undefined;
	}

	const parsedScore = parseFloat(score);
	let scoreError: DraftGradeError | undefined;

	// Run validation checks
	if (score.trim() === "") {
		scoreError = "scoreRequired";
	} else if (isNaN(parsedScore)) {
		scoreError = "invalidScore";
	} else if (parsedScore < 0) {
		scoreError = "scoreNegative";
	} else if (parsedScore > maxGrade) {
		scoreError = "scoreExceedsMax";
	}

	return scoreError;
}

export function validateDraftGrades(
	draftGrades: Record<number, DraftGrade>,
	quizResponses: QuizResponseResult[],
	quizQuestions: QuizQuestion[],
) {
	const updatedDrafts: Record<number, DraftGrade> = {};

	quizResponses.forEach((response) => {
		const currentDraft = draftGrades[response.id];
		if (!currentDraft) return; // Skip if it hasn't been initialized/edited

		// Logic taken from QuizResultPanel
		const originalQuestion = getQuestionFromResponse(response, quizQuestions);
		const maxPoints = originalQuestion ? originalQuestion.correctionPoints : 0;

		// Save the draft state with its calculated error block
		updatedDrafts[response.id] = {
			...currentDraft,
			error: validateDraftGrade(currentDraft.score, maxPoints),
		};
	});

	return updatedDrafts;
}

export function hasErrors(draftGrades: Record<number, DraftGrade>) {
	return Object.values(draftGrades).some((draft) => draft.error !== undefined);
}

export function getQuestionFromResponse(
	response: QuizResponseResult,
	quizQuestions: QuizQuestion[],
): QuizQuestion | undefined {
	return quizQuestions[response.questionNo - 1];
}

export function getQuestionFromResponseId(
	responseId: number,
	quizResponses: QuizResponseResult[],
	quizQuestions: QuizQuestion[],
): QuizQuestion | undefined {
	const response = quizResponses.find((r) => r.id === responseId);
	if (!response) return undefined;
	return getQuestionFromResponse(response, quizQuestions);
}
export function getGradeDraftFromResponse(
	draftGrades: Record<number, DraftGrade>,
	response?: QuizResponseResult,
): DraftGrade | undefined {
	if (!response) return undefined;

	return draftGrades[response.id];
}

export function getQuestionNumbersWithErrors(
	draftGrades: Record<number, DraftGrade>,
	quizResponses: QuizResponseResult[],
) {
	return quizResponses
		.filter((response) => draftGrades[response.id]?.error !== undefined)
		.map((response) => response.questionNo);
}

export class GradeValidationError extends Error {}

// Assuming there is one question and the first one is the assignment question
export function getAssignmentDraft(draftGrades: Record<number, DraftGrade>) {
	if (Object.keys(draftGrades).length === 0) {
		return undefined;
	}
	return Object.values(draftGrades)[0];
}

// Assuming there is one question and the first one is the assignment question
export function getAssignmentQuestion(quizQuestions: QuizQuestion[]) {
	return quizQuestions[0];
}

// Assuming there is one question and the first one is the assignment question
// Response id is the id of the quiz response which is the key in the draftGrades record
export function getAssignmentResponseId(draftGrades: Record<number, DraftGrade>) {
	return Number(Object.keys(draftGrades)[0]);
}

export function getMaxGradeForQuestion(question?: QuizQuestion) {
	return question?.correctionPoints ?? 0;
}

export function isGradeable(question: QuizQuestion | number | undefined) {
	if (question === undefined) {
		return false;
	}

	const grade = typeof question === "number" ? question : getMaxGradeForQuestion(question);
	return grade > 0;
}

export function getDraftGradesFromResponses(responses: QuizResponseResult[]): Record<number, DraftGrade> {
	const draftGrades = responses.reduce(
		(acc, currentResponse) => {
			acc[currentResponse.id] = {
				score: String(currentResponse.score),
				notes: currentResponse.notes ?? "",
			};
			return acc;
		},
		{} as Record<number, DraftGrade>,
	);
	return draftGrades;
}
